<template>
  <div class="playmat-picker" :class="tone">
    <label class="opacity-row">
      <span>{{ t('settings.matOpacity') }}</span>
      <input
        type="range"
        min="0"
        max="100"
        step="5"
        :value="duelPrefs.matOpacity"
        :aria-valuetext="`${duelPrefs.matOpacity}%`"
        @input="onOpacity"
      >
      <span class="pct">{{ duelPrefs.matOpacity }}%</span>
    </label>

    <button
      type="button"
      class="mat-tile random"
      :class="{ selected: duelPrefs.matId === RANDOM_MAT_ID }"
      :aria-pressed="duelPrefs.matId === RANDOM_MAT_ID"
      @click="duelPrefs.setMatId(RANDOM_MAT_ID)"
    >
      <span class="mat-dice" aria-hidden="true">⚄</span>
      <span>
        <span class="mat-name">{{ t('settings.matRandom') }}</span>
        <span class="mat-sub">{{ t('settings.matRandomHint') }}</span>
      </span>
    </button>

    <h3>{{ t('settings.matCartoon') }}</h3>
    <fieldset class="mat-grid">
      <legend class="visually-hidden">{{ t('settings.matCartoon') }}</legend>
      <button
        v-for="mat in cartoonMats"
        :key="mat.id"
        type="button"
        class="mat-tile"
        :class="{ selected: duelPrefs.matId === mat.id }"
        :aria-pressed="duelPrefs.matId === mat.id"
        :title="t(`settings.mats.${mat.id}`)"
        :aria-label="t(`settings.mats.${mat.id}`)"
        :disabled="locked(mat.id)"
        @click="pick(mat.id)"
      >
        <img :src="mat.src" alt="" />
      </button>
    </fieldset>

    <h3>{{ t('settings.matFlat') }}</h3>
    <fieldset class="mat-grid">
      <legend class="visually-hidden">{{ t('settings.matFlat') }}</legend>
      <button
        v-for="mat in flatMats"
        :key="mat.id"
        type="button"
        class="mat-tile"
        :class="{ selected: duelPrefs.matId === mat.id }"
        :aria-pressed="duelPrefs.matId === mat.id"
        :title="t(`settings.mats.${mat.id}`)"
        :aria-label="t(`settings.mats.${mat.id}`)"
        :disabled="locked(mat.id)"
        @click="pick(mat.id)"
      >
        <img :src="mat.src" alt="" />
      </button>
    </fieldset>

    <h3>{{ t('settings.leagueRewards') }}</h3>
    <fieldset class="mat-grid">
      <legend class="visually-hidden">{{ t('settings.leagueRewards') }}</legend>
      <button
        v-for="mat in leagueMats"
        :key="mat.id"
        type="button"
        class="mat-tile league"
        :class="{ selected: duelPrefs.matId === mat.id }"
        :aria-pressed="duelPrefs.matId === mat.id"
        :title="tileTitle(mat.id)"
        :aria-label="tileTitle(mat.id)"
        :disabled="locked(mat.id)"
        @click="pick(mat.id)"
      >
        <img :src="mat.src" alt="" />
        <img
          v-if="locked(mat.id)"
          class="league-lock"
          :src="badgeOf(mat.id)"
          alt=""
        />
      </button>
    </fieldset>
  </div>
</template>

<script setup lang="ts">
import { LAST_CALL_MAT_ID, leagueBadgeSrc, leagueOfMat } from '@shared/index'
import { CARTOON_MATS, FLAT_MATS, LEAGUE_MATS, RANDOM_MAT_ID } from '../game/gamemats'
import { useDuelPrefsStore } from '../stores/duelPrefs'
import { useLastCallStore } from '../stores/lastCall'
import { useLeagueRewardsStore } from '../stores/leagueRewards'
import { useI18n } from '../i18n/t'

withDefaults(
  defineProps<{ tone?: 'duel' | 'menu' }>(),
  { tone: 'duel' },
)

const { t } = useI18n()
const duelPrefs = useDuelPrefsStore()
const lastCall = useLastCallStore()
const leagueRewards = useLeagueRewardsStore()
const cartoonMats = CARTOON_MATS
const flatMats = FLAT_MATS
const leagueMats = LEAGUE_MATS

function locked(id: string): boolean {
  if (id === LAST_CALL_MAT_ID) return !lastCall.matUnlocked
  return !leagueRewards.matUnlocked(id)
}

function badgeOf(id: string): string {
  return leagueBadgeSrc(leagueOfMat(id)) ?? ''
}

function tileTitle(id: string): string {
  const name = t(`settings.mats.${id}`)
  const league = leagueOfMat(id)
  if (!league || !locked(id)) return name
  return `${name} · ${t('settings.leagueLocked', { league: t(`rank.leagues.${league}`) })}`
}

function pick(id: string): void {
  if (locked(id)) return
  duelPrefs.setMatId(id)
}

function onOpacity(event: Event): void {
  const el = event.target as HTMLInputElement
  duelPrefs.setMatOpacity(Number(el.value))
}
</script>

<style scoped>
.playmat-picker {
  width: 100%;
  color: #e2e8f0;
}

.opacity-row {
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto auto;
  gap: 4px 8px;
  margin: 0 0 12px;
  font-size: 12px;
  font-weight: 500;
}

.opacity-row input {
  grid-column: 1 / -1;
  width: 100%;
  accent-color: var(--md-sys-color-primary, #c9a227);
}

.pct {
  color: #94a3b8;
  font-variant-numeric: tabular-nums;
}

h3 {
  margin: 12px 0 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #94a3b8;
}

.mat-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  margin: 0;
  padding: 0;
  border: 0;
}

.mat-tile {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 4px;
  border: 2px solid transparent;
  border-radius: 8px;
  background: #1e293b;
  color: inherit;
  font: inherit;
  cursor: pointer;
}

.mat-tile img {
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  border-radius: 4px;
}

.mat-tile.selected {
  border-color: #e8c547;
}

.mat-tile.league {
  position: relative;
}

.mat-tile.league:disabled {
  opacity: 1;
}

.mat-tile.league:disabled img:first-child {
  opacity: 0.4;
}

.mat-tile .league-lock {
  position: absolute;
  right: 4px;
  bottom: 4px;
  width: 34%;
  aspect-ratio: 1;
  object-fit: contain;
}

.mat-tile.random {
  width: 100%;
  margin-bottom: 4px;
  padding: 8px;
  text-align: left;
}

.mat-dice {
  font-size: 22px;
  line-height: 1;
}

.mat-name {
  display: block;
  font-size: 13px;
  font-weight: 500;
}

.mat-sub {
  display: block;
  font-size: 11px;
  color: #94a3b8;
}

.menu {
  color: var(--md-sys-color-on-surface);
}

.menu .pct,
.menu .mat-sub,
.menu h3 {
  color: var(--md-sys-color-on-surface-variant);
}

.mat-tile:disabled {
  cursor: default;
  opacity: 0.45;
}

.menu .mat-tile {
  background: var(--md-sys-color-surface-container-high);
}

.menu .mat-tile.selected {
  border-color: var(--md-sys-color-primary);
}

.menu .mat-grid {
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}
</style>
